<script setup lang="ts">
import { useThrottleFn } from '@vueuse/core';
import { onMounted, ref, toRefs } from 'vue';

import IconMail from '@/components/icons/IconMail.vue';
import IconSearchBig from '@/components/icons/IconSearchBig.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import IconTrash from '@/components/icons/IconTrash.vue';
import Loader from '@/components/ui/Loader.vue';
import router from '@/router';
import { useThreadStore } from '@/stores/thread';
import { useUserStore } from '@/stores/user';
import { getScrolledPercentage } from '@/utils/dom';
import ThreadRow from '@/views/home/components/ThreadRow.vue';

const userStore = useUserStore();
const threadStore = useThreadStore();
const { currentUser } = toRefs(userStore);
const { threads, loading, selectedTab, searchParams } = toRefs(threadStore);

const scrollSection = ref();

const redirectToThread = async (uuid: string, threadUuid?: string) => {
  if (selectedTab.value === 'sent') {
    await router.push(`/thread/${threadUuid}?email_uuid=${uuid}`);
  } else {
    await router.push({ name: 'thread', params: { uuid: uuid } });
  }
};

const refresh = async ({ searchParams, tab }) => {
  if (searchParams !== undefined) {
    threadStore.setSearchParams(searchParams);
  }

  if (tab) {
    threadStore.setSelectedTab(tab);
  }

  threadStore.clearState();
  await threadStore.getThreadList();
};

const loadMoreData = useThrottleFn(async () => {
  if (loading.value) {
    return;
  }

  await threadStore.getThreadList();
}, 350);

const handleScroll = async () => {
  if (getScrolledPercentage(scrollSection.value) > 80) {
    await loadMoreData();
  }
};

onMounted(() => {
  scrollSection.value.addEventListener('scroll', handleScroll);
});

defineExpose({
  refresh
});
</script>

<template>
  <div
    ref="scrollSection"
    class="threads-wrapper"
  >
    <div
      class="thread-list"
      :class="{ 'no-data': !loading && !threads.length }"
    >
      <template v-if="threads.length">
        <ThreadRow
          v-for="(item, i) in threads"
          :key="`${item.name}-${i}`"
          :row="item"
          @click="redirectToThread(item.uuid, item?.threadUuid)"
        />
        <Loader
          v-if="loading"
          class="infinite-loader"
        />
      </template>
      <div v-if="!loading && !threads.length">
        <template v-if="!!searchParams">
          <div class="flex-center no-data">
            <div class="icon-wrapper flex-center">
              <IconSearchBig />
            </div>
            <p class="no-data__title">
              {{ $t('home.noData.search.title') }}
            </p>
            <p class="no-data__description">
              {{ $t('home.noData.search.description') }}
            </p>
          </div>
        </template>
        <template v-else>
          <template v-if="selectedTab !== 'trash'">
            <template v-if="currentUser?.company?.supportEmail">
              <div class="flex-center no-data">
                <div class="icon-wrapper flex-center">
                  <IconMail />
                </div>
                <p class="no-data__title">
                  {{ $t('home.noData.title') }}
                </p>
                <p class="no-data__description">
                  {{ $t('home.noData.description') }}
                </p>
              </div>
            </template>
            <template v-else>
              <div class="flex-center no-data">
                <div class="icon-wrapper flex-center">
                  <IconSettings />
                </div>
                <p class="no-data__title">
                  {{ $t('home.noData.ctaOutbound.title') }}
                </p>
                <p class="no-data__description"
                   v-html="$t('home.noData.ctaOutbound.description', {
                     desc1: $t('home.noData.ctaOutbound.description1'),
                     desc2: $t('home.noData.ctaOutbound.description2'),
                     desc3: $t('home.noData.ctaOutbound.description3'),
                     email: currentUser?.config?.defaultFromEmail
                   })"
                ></p>
              </div>
            </template>
          </template>
          <template v-else>
            <div class="flex-center no-data">
              <div class="icon-wrapper flex-center">
                <IconTrash />
              </div>
              <p class="no-data__title">
                {{ $t('home.noData.trash.title') }}
              </p>
              <p class="no-data__description"
                 v-html="$t('home.noData.trash.description')"
              ></p>
            </div>
          </template>
        </template>
      </div>
      <div
        v-if="loading && !threads.length"
        class="loader-wrapper"
      >
        <Loader />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.thread-list {
  border-radius: 4px;
  overflow: hidden;
  background-color: var(--white);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  &.no-data {
    background-color: transparent;
  }
}

.threads-wrapper {
  padding: 0 30px;
  overflow: auto;
  height: calc(100dvh - 251px);
}
</style>

<style lang="scss">
.loader-wrapper {
  margin: 100px auto;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.no-data {
  flex-direction: column;
  gap: 15px;
  padding: 30px 0;
}

.no-data__title {
  font-size: 20px;
  font-weight: 800;
}

.no-data__description {
  font-size: 14px;
  max-width: 310px;
  text-align: center;
}

.infinite-loader {
  margin: 30px 0;
}

.icon-wrapper {
  padding: 16px;
  border-radius: 50%;
  background-color: var(--border-default);
}
</style>
